import * as React from 'react';
import format from 'date-fns/format';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableRow,
  tableCellClasses,
} from '@mui/material';
import { ITEM_CONFIG } from './constants';
import EditableField from '../../../shared/ui/EditableField/EditableField';
import { usePatchLogisticTaskMutation } from '../../../entities/logistic/logisticTasks/redux/logisticTasks.api';
import { useAppSelector } from '../../../app/store';
import reducerPath from '../../../app/reducerPath';

export default function DetailsDialog(props) {
  const { type, item, closeItemDialog, onOk, okLabel } = props;
  const [fields, setFields] = React.useState([]);
  const [title, setTitle] = React.useState('');

  const {
    data: { logisticTaskId, source },
  } = useAppSelector((state) => state[`${reducerPath.logisticTasksBalanceResource}/counter`]);

  const [patchLogisticTask] = usePatchLogisticTaskMutation();

  const handleChangeFieldValue = (fieldName, newValue) => {
    if (type !== 'field') return;

    setFields((prevFields) => prevFields.map((fld) => (fld.name === fieldName ? { ...fld, value: newValue } : fld)));

    const updatedFieldList = source.fieldDetailsList?.map((fld) =>
      fld.geozoneId === item.geozoneId ? { ...fld, [fieldName]: newValue } : fld,
    );

    patchLogisticTask({
      id: logisticTaskId,
      body: {
        logisticTaskId,
        source: {
          fieldDetailsList: updatedFieldList,
        },
      },
    });
  };

  React.useEffect(() => {
    if (!!item && type) {
      const config = ITEM_CONFIG[type];
      setTitle(config.title);
      setFields(
        config.fields.map(({ name, label, isList, isDate, isEditable }) => ({
          value: isList ? item[name].join(', ') : isDate ? format(new Date(item[name]), 'dd.MM.yyyy') : item[name],
          label,
          name,
          isEditable,
        })),
      );
    }
  }, [type, item]);

  const handleOnOk = () => {
    onOk?.();
    closeItemDialog();
  };

  return (
    <Dialog
      maxWidth="md"
      open={!!item}
      onClose={closeItemDialog}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
      <DialogContent sx={{ paddingBottom: 0 }}>
        <Table
          sx={{
            [`& .${tableCellClasses.root}`]: {
              borderBottom: 'none',
            },
          }}
          size="small"
        >
          <TableBody>
            {fields.map((fld, i) => (
              <TableRow
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                sx={{ border: 0 }}
                hover
              >
                <TableCell sx={{ color: (theme) => theme.palette.text.secondary }}>{fld.label}</TableCell>
                {fld.isEditable ? (
                  <TableCell sx={{ display: 'flex', justifyContent: 'start', minHeight: '42px' }}>
                    <EditableField
                      value={fld.value}
                      onChange={(newValue) => handleChangeFieldValue(fld.name, newValue)}
                      width="80px"
                    />
                  </TableCell>
                ) : (
                  <TableCell>{fld.value}</TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeItemDialog}>Отмена</Button>
        {onOk && (
          <Button onClick={handleOnOk} autoFocus>
            {okLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

DetailsDialog.propTypes = {
  item: PropTypes.oneOfType([
    PropTypes.shape(
      ITEM_CONFIG.harvester.fields.reduce(
        (acc, field) => ({
          ...acc,
          [field.name]: field.type,
        }),
        {},
      ),
    ),
    PropTypes.shape(
      ITEM_CONFIG.vehicle.fields.reduce(
        (acc, field) => ({
          ...acc,
          [field.name]: field.type,
        }),
        {},
      ),
    ),
    PropTypes.shape(
      ITEM_CONFIG.field.fields.reduce(
        (acc, field) => ({
          ...acc,
          [field.name]: field.type,
        }),
        {},
      ),
    ),
    PropTypes.shape(
      ITEM_CONFIG.warehouse.fields.reduce(
        (acc, field) => ({
          ...acc,
          [field.name]: field.type,
        }),
        {},
      ),
    ),
  ]),
  closeItemDialog: PropTypes.func,
  onOk: PropTypes.func,
  okLabel: PropTypes.string,
  type: PropTypes.oneOf(['harvester', 'vehicle', 'field', 'warehouse']).isRequired,
};

DetailsDialog.defaultProps = {
  item: {},
  closeItemDialog: null,
  onOk: null,
  okLabel: 'Да',
};
